import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { Money } from "@/components/HideValues";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileEdit, Receipt, Wrench, CheckCircle2, Lock, Wallet, Target, Pencil, XCircle } from "lucide-react";

const CARDS = [
  { k: "pre_budget", label: "Pré-orçamento", icon: FileEdit, to: "/owner/ordens", color: "#a1a1aa", val: "pre_budget_value" },
  { k: "budget", label: "Orçamento", icon: Receipt, to: "/owner/funil", color: "#facc15", val: "budget_value" },
  { k: "in_progress", label: "Em andamento", icon: Wrench, to: "/owner/patio", color: "#38bdf8", val: "in_progress_value" },
  { k: "finished_today", label: "Finalizados no dia", icon: CheckCircle2, to: "/owner/patio", color: "#4ade80", val: "finished_value", valLabel: "No dia" },
  { k: "blocked", label: "Travados", icon: Lock, to: "/owner/patio", color: "#f87171", sub: "blocked_value", val: "blocked_value" },
  { k: "cash_today", label: "Caixa do dia", icon: Wallet, to: "/owner/financeiro", color: "#4ade80", money: true },
  { k: "refused", label: "Recusados", icon: XCircle, to: "/owner/ordens?status=recusado", color: "#ef4444" },
];
const SRC = { google: "Google", indicacao: "Indicação", pos_venda: "Pós-venda", instagram: "Instagram", facebook: "Facebook", ja_cliente: "Já é cliente", outro: "Outro", nao_informado: "Não informado" };

export function usePanel(from, to) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/panel/summary", { params: from || to ? { from, to } : {} }).then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, [user?.shop_id, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  return [d, load];
}

export function TopCards({ d, showValues = false, singleDay = true }) {
  const c = d?.cards || {};
  return (
    <div data-testid="panel-cards" className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
      {CARDS.map(({ k, label, icon: Icon, to, color, money, sub: sub0, val, valLabel }) => { const sub = showValues ? null : sub0; return (
        <Link key={k} to={to} data-testid={`panel-card-${k}`} className="rounded-2xl lg:rounded-xl border border-card2-border bg-card2 hover:border-zinc-600 transition-colors p-4 lg:p-3 flex flex-col gap-2 lg:gap-1.5">
          <span className="h-9 w-9 lg:h-7 lg:w-7 rounded-xl lg:rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}><Icon className="h-5 w-5 lg:h-4 lg:w-4" /></span>
          <span data-testid={`panel-card-${k}-value`} className="font-display text-3xl lg:text-2xl font-black text-zinc-50 leading-none">{d ? (money ? <Money value={c[k]} /> : c[k] ?? 0) : "—"}</span>
          {showValues && val && <span data-testid={`panel-card-${k}-amount`} className="text-[11px] text-zinc-400 leading-none">{valLabel ? (singleDay ? valLabel : "No período") : "Total"}: {d ? <Money value={c[val] ?? 0} /> : "—"}</span>}
          <span className="text-xs text-zinc-400">{label}{sub && c[sub] ? <> · <Money value={c[sub]} /> parado</> : ""}</span>
        </Link>
      ); })}
    </div>
  );
}

export function GoalBar({ d, onSaved }) {
  const { user } = useAuth();
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState("");
  const [ev, setEv] = useState("");
  const g = d?.goal;
  const save = async () => {
    try { await api.put("/shop-settings/revenue-goal", { monthly_goal: v === "" ? null : Number(v), monthly_expense_goal: ev === "" ? null : Number(ev) }); toast.success("Meta salva"); setEdit(false); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const canEdit = user?.shop_id !== "all";
  return (
    <div data-testid="goal-bar" className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-bold text-zinc-100"><Target className="h-4 w-4 text-green-brand" /> Meta de faturamento do mês</span>
        {canEdit && <button type="button" data-testid="goal-edit" onClick={() => { setV(g?.goal || ""); setEv(g?.expense_goal || ""); setEdit((x) => !x); }} className="text-zinc-400 hover:text-green-neon"><Pencil className="h-4 w-4" /></button>}
      </div>
      {edit && <div className="flex flex-wrap items-end gap-2">
        <label className="text-[11px] text-zinc-400">Meta mensal de faturamento (R$)<Input data-testid="goal-input" type="number" value={v} onChange={(e) => setV(e.target.value)} placeholder="Ex.: 50000" className="mt-1 bg-zinc-900 border-zinc-800 w-[200px]" /></label>
        <label className="text-[11px] text-zinc-400">Meta mensal de despesas (R$)<Input data-testid="expense-goal-input" type="number" value={ev} onChange={(e) => setEv(e.target.value)} placeholder="Ex.: 20000" className="mt-1 bg-zinc-900 border-zinc-800 w-[200px]" /></label>
        <Button data-testid="goal-save" size="sm" onClick={save} className="bg-green-brand text-white">Salvar</Button></div>}
      {!g?.goal ? <p className="text-xs text-zinc-500">Defina uma meta mensal para acompanhar o progresso.</p> : (
        <>
          <div className="h-3 rounded-full bg-zinc-900 overflow-hidden"><div data-testid="goal-progress" className="h-full bg-green-brand" style={{ width: `${Math.min(100, g.pct || 0)}%` }} /></div>
          <div className="flex flex-wrap justify-between gap-2 text-xs text-zinc-400"><span data-testid="goal-text"><Money value={g.realized} /> de <Money value={g.goal} /> · {g.pct}%</span><span>{g.missing > 0 ? <>Faltam <Money value={g.missing} /></> : "Meta batida!"}</span></div>
        </>
      )}
    </div>
  );
}

function Bars({ rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div data-testid={testid} className="space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="text-xs">
          <div className="flex justify-between text-zinc-300"><span>{r.label}</span><span>{r.text ?? r.value}</span></div>
          <div className="h-2 rounded-full bg-zinc-900 mt-1"><div className="h-full rounded-full" style={{ width: `${(r.value / max) * 100}%`, background: r.color || "var(--c-brand)" }} /></div>
        </div>
      ))}
      {!rows.length && <p className="text-xs text-zinc-500">Sem dados no mês.</p>}
    </div>
  );
}

export function PanelCharts({ d }) {
  const [mech, setMech] = useState("");
  if (!d) return null;
  const rw = d.rework.filter((r) => !mech || r.mechanic === mech);
  const tot = rw.reduce((s, r) => s + r.orders, 0), rwc = rw.reduce((s, r) => s + r.rework, 0);
  const box = "rounded-2xl border border-card2-border bg-card2 p-4 space-y-3";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className={box}><div className="text-xs font-mono-caps text-zinc-500">Canais de venda (OS faturadas no mês)</div>
        <Bars testid="chart-channels" rows={d.channels.map((c) => ({ label: SRC[c.source] || c.source, value: c.count, text: `${c.count} · ${c.pct}%` }))} /></div>
      <div className={box} data-testid="rework-box">
        <div className="flex items-center justify-between gap-2"><span className="text-xs font-mono-caps text-zinc-500">Retrabalho (mês)</span>
          <select data-testid="rework-mechanic" value={mech} onChange={(e) => setMech(e.target.value)} className="h-8 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-xs text-zinc-200">
            <option value="">Todos</option>{d.rework.map((r) => <option key={r.mechanic} value={r.mechanic}>{r.mechanic}</option>)}</select></div>
        <div data-testid="rework-total" className="font-display text-3xl font-black text-zinc-50">{rwc} <span className="text-base text-zinc-400">de {tot} OS · {tot ? Math.round((rwc / tot) * 100) : 0}%</span></div>
      </div>
    </div>
  );
}
